import { Send, Sparkles, Users, Bot, Calendar } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";

export default function Hero() {
  const { openModal } = useLeadModal();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-12 sm:pt-32 sm:pb-20 lg:pt-36 lg:pb-24"
    >
      <div className="mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
        {/* Top badge */}
        <div className="mb-6 flex justify-center">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-1.5 backdrop-blur-sm"
          >
            <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
            <span className="font-heading text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Software Aurum AI · Trading Automatico
            </span>
          </div>
        </div>

        {/* H1 — short, punchy */}
        <h1
          data-testid="hero-title"
          className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5rem]"
        >
          Ti presento <span className="text-gradient-gold">Aurum AI</span>
        </h1>

        {/* Sub-headline */}
        <p
          data-testid="hero-subheadline"
          className="mx-auto mt-5 max-w-3xl font-heading text-lg font-semibold leading-snug text-gray-100 sm:mt-6 sm:text-2xl lg:text-3xl"
        >
          <span className="text-gradient-gold">Aurum AI</span>, il software di trading
          automatizzato sviluppato da TheGoldFx. Una community di oltre <span className="text-gradient-cyan">15.000 persone</span>.
        </p>

        {/* VSL video */}
        <div
          data-testid="hero-vsl-wrapper"
          className="luminous-border mx-auto mt-6 max-w-2xl overflow-hidden rounded-2xl bg-black sm:mt-7"
        >
          <video
            data-testid="hero-vsl-video"
            className="block h-auto w-full"
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_algo-profits-4/artifacts/o9xt4awl_VSL_Carlo.mp4"
            controls
            playsInline
            preload="metadata"
          />
        </div>

        {/* CTA primary — right under the VSL video */}
        <button
          type="button"
          onClick={openModal}
          data-testid="hero-primary-cta"
          className="group mx-auto mt-6 flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600 px-7 py-5 font-heading text-base font-black uppercase tracking-wider text-black shadow-[0_0_40px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(255,215,0,0.75)] sm:mt-7 sm:text-lg"
        >
          <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          Scopri di più
        </button>

        {/* Second sub-headline */}
        <p
          data-testid="hero-subheadline-2"
          className="mx-auto mt-4 max-w-3xl font-body text-base text-gray-300 sm:mt-5 sm:text-lg"
        >
          <span className="font-semibold text-yellow-300">Online dal 2020</span>, non un software nato ieri.
          Una volta configurato, <span className="font-semibold text-cyan-300">Aurum AI</span> esegue automaticamente la strategia secondo parametri prestabiliti.
        </p>

        {/* Stats — 4 cols desktop / 2x2 mobile */}
        <div className="mt-7 sm:mt-8">
          <div
            data-testid="hero-stat-strip"
            className="luminous-border grid grid-cols-2 gap-y-4 divide-white/10 rounded-3xl p-5 sm:grid-cols-4 sm:gap-y-0 sm:divide-x sm:p-6"
          >
            <div className="px-2 text-center" data-testid="hero-stat-setup">
              <div className="flex items-center justify-center">
                <Send className="h-4 w-4 text-yellow-300 sm:h-5 sm:w-5" />
              </div>
              <div className="mt-1.5 font-heading text-xl font-black text-white sm:text-2xl lg:text-3xl">
                ~10 min
              </div>
              <div className="mt-0.5 font-body text-[9px] font-medium uppercase tracking-wider text-gray-400 sm:text-[10px]">
                Collegamento guidato
              </div>
            </div>
            <div className="px-2 text-center" data-testid="hero-stat-subscribers">
              <div className="flex items-center justify-center">
                <Users className="h-4 w-4 text-yellow-300 sm:h-5 sm:w-5" />
              </div>
              <div className="mt-1.5 font-heading text-xl font-black text-gradient-gold sm:text-2xl lg:text-3xl">
                +15.000
              </div>
              <div className="mt-0.5 font-body text-[9px] font-medium uppercase tracking-wider text-gray-400 sm:text-[10px]">
                Persone in community
              </div>
            </div>
            <div
              className="px-2 text-center border-t border-white/10 pt-4 sm:border-t-0 sm:pt-0"
              data-testid="hero-stat-uptime"
            >
              <div className="flex items-center justify-center">
                <Bot className="h-4 w-4 text-cyan-300 sm:h-5 sm:w-5" />
              </div>
              <div className="mt-1.5 font-heading text-xl font-black text-gradient-cyan sm:text-2xl lg:text-3xl">
                24/5
              </div>
              <div className="mt-0.5 font-body text-[9px] font-medium uppercase tracking-wider text-gray-400 sm:text-[10px]">
                Operatività automatizzata
              </div>
            </div>
            <div
              className="px-2 text-center border-t border-white/10 pt-4 sm:border-t-0 sm:pt-0"
              data-testid="hero-stat-since"
            >
              <div className="flex items-center justify-center">
                <Calendar className="h-4 w-4 text-yellow-300 sm:h-5 sm:w-5" />
              </div>
              <div className="mt-1.5 font-heading text-xl font-black text-gradient-gold sm:text-2xl lg:text-3xl">
                Dal 2020
              </div>
              <div className="mt-0.5 font-body text-[9px] font-medium uppercase tracking-wider text-gray-400 sm:text-[10px]">
                Sui mercati
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
